import { t } from "@lingui/macro";
import { useIsFocused } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container/Container";
import { CardScroller } from "@whoosh/design-system/src/components/display";
import { Text } from "@whoosh/design-system/src/components/primitive";

import { BookingCard } from "../../../features/BookingCard";
import { BookingCard_Booking$key } from "../../../features/BookingCard/__generated__/BookingCard_Booking.graphql";
import { compareDateTime } from "../../../utils/sort";
import { CustomerSchedule_Bookings$key } from "./__generated__/CustomerSchedule_Bookings.graphql";

type CustomerScheduleProps = {
  customerBookingsRef: CustomerSchedule_Bookings$key | null;
};

const getDateTimeParts = (dateTime: string | null | undefined) => {
  const [date = "", rawTime = ""] = dateTime?.split(/[T ]/) ?? [];
  const time = rawTime
    .replace(/\.\d+Z?$/, "")
    .replace(/Z$/, "")
    .slice(0, 8);

  return { date, time };
};

export const CustomerSchedule = ({
  customerBookingsRef,
}: CustomerScheduleProps) => {
  const memberBookings = useFragment(
    CustomerScheduleBookingsFragmentString,
    customerBookingsRef
  );

  // this component sometimes tries to update itself when not focused
  // resulting in errors of multiple components trying to update at the same time
  // this stops that
  const isFocused = useIsFocused();
  if (!isFocused) return null;

  const allBookings = [
    ...(memberBookings?.bookings?.edges || []),
    ...(memberBookings?.managedBookings?.edges || []),
  ];

  const bookingsList =
    allBookings?.map((booking) => {
      const startSlotDateTime = getDateTimeParts(
        booking?.node?.startSlot?.dateTime
      );
      const firstAgendaDateTime = getDateTimeParts(
        booking?.node?.startSlot?.agenda?.timeSlots?.firstDateTime
      );

      return {
        type: booking?.node?.__typename,
        id: booking?.node?.id || "",
        date:
          startSlotDateTime.date ||
          booking?.node?.startSlot?.agenda.date ||
          firstAgendaDateTime.date ||
          "",
        time:
          startSlotDateTime.time ||
          booking?.node?.startSlot?.time ||
          firstAgendaDateTime.time ||
          "",
        ref: booking?.node as BookingCard_Booking$key,
      };
    }) || [];

  const sortedBookings = bookingsList.sort(compareDateTime);

  return (
    <Container pb={4} paddingHorizontal={{ base: 0, lg: 12 }}>
      <CardScroller
        data={sortedBookings}
        paddingHorizontal={{ base: 6, md: 16, lg: 0 }}
        titleElement={
          <Text
            variant={{
              base: "mainHeading",
              lg: "mainTitle2",
            }}
            mb={{ base: 0, lg: 3 }}
            px={{ base: 6, md: 16, lg: 0 }}
          >
            {t`Your Schedule`}
          </Text>
        }
        renderItem={(item) =>
          item?.ref ? (
            <BookingCard
              BookingCardRef={(item.ref as BookingCard_Booking$key) ?? null}
            />
          ) : null
        }
      />
    </Container>
  );
};

export const CustomerScheduleBookingsFragmentString = graphql`
  fragment CustomerSchedule_Bookings on Customer {
    id
    club {
      departments {
        count
      }
    }
    bookings(
      dateTimeGte: $dateTimeGTE
      first: 100
      orderBy: { field: DATE_TIME, direction: ASC }
    ) {
      count
      edges {
        node {
          __typename
          startSlot {
            agenda {
              date
              timeSlots {
                firstDateTime
              }
            }
            ... on TimeSlot {
              dateTime
              time
            }
          }
          id
          ...BookingCard_Booking
        }
      }
    }
    managedBookings(
      dateTimeGte: $dateTimeGTE
      first: 100
      orderBy: { field: DATE_TIME, direction: ASC }
    ) {
      count
      edges {
        node {
          __typename
          startSlot {
            agenda {
              date
              timeSlots {
                firstDateTime
              }
            }
            ... on TimeSlot {
              dateTime
              time
            }
          }
          id
          ...BookingCard_Booking
        }
      }
    }
  }
`;
